import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BellPlus, Check, Grid2X2, Sparkles, X } from 'lucide-react';

import { useConfiguration } from '../../../context/configurationContext';
import { Page } from '../../../components';
import { isScrollingDisabled } from '../../../ducks/ui.duck';
import { useSelector } from 'react-redux';
import TopbarContainer from '../../TopbarContainer/TopbarContainer';
import FooterContainer from '../../FooterContainer/FooterContainer';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

import { NuqsAdapter } from '../../../util/nuqsAdapterV5';
import {
  useSearchPage,
  SORT_OPTIONS,
  CONDITION_GROUPS,
  PRICE_PRESETS,
  QUICK_CATEGORIES,
} from './useSearchPage';
import { MarketplaceListingCard } from '../../../components/MarketplaceListingCard';
import { QuickFiltersSheet } from '../../../components/QuickFiltersSheet';
import { useNearMe, NEAR_ME_STATUS } from '../../../hooks/useNearMe';
import { useLocationFilter, DEVICE_LOCATION_LABEL } from '../../../ducks/locationFilter.duck';
import { mapListingToCardProps } from '../../../util/listingCardAdapter';
import {
  useCreateSavedSearchMutation,
  useFetchSavedSearchesQuery,
} from '../../../ducks/savedSearches.duck';
import {
  paramsToSignature,
  savedSearchToSignature,
} from '../../../util/savedSearchSignature';

const DEFAULT_DISTANCE_MILES = 25;
const DEFAULT_LOCATION_LABEL = 'Near me';

const SAVE_RESET_MS = 2500;

function CustomSearchPageInner() {
  const config = useConfiguration() as any;
  const scrollingDisabled = useSelector((state: any) => isScrollingDisabled(state));
  const isAuthenticated = useSelector((state: any) => state?.auth?.isAuthenticated);

  const { params, setParams, listings, pagination, searchInProgress } = useSearchPage();

  const [createSavedSearch, createSavedSearchState] = useCreateSavedSearchMutation();
  const [justSaved, setJustSaved] = useState(false);
  useEffect(() => {
    if (!justSaved) return;
    const t = window.setTimeout(() => setJustSaved(false), SAVE_RESET_MS);
    return () => window.clearTimeout(t);
  }, [justSaved]);

  const { data: savedSearchesData } = useFetchSavedSearchesQuery(
    { page: 1, limit: 100 },
    { skip: !isAuthenticated }
  );

  const currentSignature = useMemo(() => paramsToSignature(params as any), [params]);
  const alreadySaved = useMemo(() => {
    const items = savedSearchesData?.items ?? [];
    return items.some(s => savedSearchToSignature(s) === currentSignature);
  }, [savedSearchesData, currentSignature]);

  const handleSaveSearch = async () => {
    const filters_json: Record<string, unknown> = {};
    if (params.pub_condition) filters_json.pub_condition = params.pub_condition;
    if (params.price) filters_json.price = params.price;
    if (params.sort) filters_json.sort = params.sort;
    if (params.address) filters_json.address = params.address;
    if (params.origin) filters_json.origin = params.origin;
    if (params.distance) filters_json.distance = params.distance;
    if (params.bounds) filters_json.bounds = params.bounds;
    const category_filter = params.pub_categoryLevel1 ? [params.pub_categoryLevel1] : null;
    const keywords = (draftKeywords ?? params.keywords ?? '').trim();
    const label = keywords || params.pub_categoryLevel1 || 'Saved search';
    const result = await createSavedSearch({
      label: label.slice(0, 120),
      query_text: keywords,
      filters_json,
      category_filter,
    });
    if ('data' in result) setJustSaved(true);
  };

  const categories: Array<{ id: string; name: string }> =
    config?.categoryConfiguration?.categories || [];

  const [draftKeywords, setDraftKeywords] = useState(params.keywords);

  const [pendingMiles, setPendingMiles] = useState<number | null>(null);

  const activeConditionValues = useMemo(
    () => (params.pub_condition ? params.pub_condition.replace(/^has_any:/, '').split(',') : []),
    [params.pub_condition]
  );

  const activeConditionGroupLabel = useMemo(() => {
    const match = CONDITION_GROUPS.find(g =>
      g.values.every(v => activeConditionValues.includes(v)) &&
      g.values.length === activeConditionValues.length
    );
    return match?.label ?? null;
  }, [activeConditionValues]);

  const activePriceLabel = useMemo(() => {
    const match = PRICE_PRESETS.find(p => p.value === params.price);
    return match && match.value !== '' ? match.label : null;
  }, [params.price]);

  const activeCategory = useMemo(
    () => categories.find(c => c.id === params.pub_categoryLevel1) ?? null,
    [categories, params.pub_categoryLevel1]
  );

  const activeFilterCount =
    (params.pub_categoryLevel1 ? 1 : 0) +
    (activeConditionValues.length > 0 ? 1 : 0) +
    (params.price ? 1 : 0) +
    (params.origin ? 1 : 0);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setParams({ keywords: draftKeywords.trim(), page: 1 }, { history: 'push' });
  };

  const handleClearSearch = () => {
    setDraftKeywords('');
    setParams({ keywords: '', page: 1 });
  };

  const setCategory = (id: string) => setParams({ pub_categoryLevel1: id, page: 1 });
  const clearCategory = () => setParams({ pub_categoryLevel1: '', page: 1 });

  const setConditionGroup = (group: (typeof CONDITION_GROUPS)[number] | null) => {
    if (!group) {
      setParams({ pub_condition: '', page: 1 });
    } else {
      setParams({ pub_condition: `has_any:${group.values.join(',')}`, page: 1 });
    }
  };

  const setPrice = (value: string) => setParams({ price: value, page: 1 });

  const nearMe = useNearMe();
  const locationFilter = useLocationFilter();

  const hydratedFromReduxRef = useRef(false);
  useEffect(() => {
    if (hydratedFromReduxRef.current) return;
    hydratedFromReduxRef.current = true;
    if (!params.origin && locationFilter.isActive && locationFilter.originString) {
      setParams({
        origin: locationFilter.originString,
        distance: String(locationFilter.distanceMiles ?? DEFAULT_DISTANCE_MILES),
        bounds: '',
        address: locationFilter.label ?? DEFAULT_LOCATION_LABEL,
        page: 1,
      });
    }
  }, [locationFilter, params.origin, setParams]);

  useEffect(() => {
    if (params.origin && params.distance) {
      const [latStr, lngStr] = params.origin.split(',');
      const lat = Number(latStr);
      const lng = Number(lngStr);
      const miles = Number(params.distance);
      if (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        (locationFilter.originString !== params.origin ||
          locationFilter.distanceMiles !== miles)
      ) {
        locationFilter.setTyped({ lat, lng, label: params.address || DEFAULT_LOCATION_LABEL });
        locationFilter.setDistance(miles);
      }
    } else if (!params.origin && locationFilter.isActive) {
      locationFilter.clear();
    }
  }, [params.origin, params.distance, params.address, locationFilter]);

  useEffect(() => {
    if (nearMe.status === NEAR_ME_STATUS.Granted && nearMe.coords && pendingMiles) {
      setParams({
        origin: `${nearMe.coords.latitude},${nearMe.coords.longitude}`,
        distance: String(pendingMiles),
        bounds: '',
        address: DEFAULT_LOCATION_LABEL,
        page: 1,
      });
      setPendingMiles(null);
    }
  }, [nearMe.status, nearMe.coords, pendingMiles, setParams]);

  const setDistance = (miles: number) => {
    setPendingMiles(miles);
  };

  const dismissNearMePrompt = () => {
    nearMe.dismiss();
    setPendingMiles(null);
  };

  const showNearMeGate =
    pendingMiles !== null &&
    nearMe.status !== NEAR_ME_STATUS.Granted &&
    nearMe.status !== NEAR_ME_STATUS.Asking &&
    !nearMe.isPersistentlyDeclined;

  const isLocationActive = !!params.origin && !!params.distance;

  const clearLocation = () =>
    setParams({ origin: '', distance: '', address: '', page: 1 });

  const clearAllFilters = () =>
    setParams({
      keywords: '',
      pub_categoryLevel1: '',
      pub_condition: '',
      price: '',
      address: '',
      origin: '',
      distance: '',
      bounds: '',
      sort: '',
      page: 1,
    });

  const totalItems = pagination?.totalItems ?? null;

  return (
    <Page title="Search" scrollingDisabled={scrollingDisabled}>
      <TopbarContainer />

      <div className="flex flex-col w-full flex-grow bg-background">
        <main className="flex-grow">

          <section className="border-b border-border bg-background">
            <div className="container mx-auto px-4 py-5 md:py-6">
              <div className="mb-4">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-heading uppercase tracking-[0.2em] text-primary">
                  <Grid2X2 className="h-3.5 w-3.5" />
                  ALL LISTINGS
                </div>
                <h1 className="mt-3 text-2xl md:text-3xl font-heading font-bold uppercase tracking-tight text-foreground">
                  Search Materials, Tools &amp; Equipment
                </h1>
                <p className="mt-2 max-w-3xl text-sm text-muted-foreground md:text-base">
                  Browse active listings across materials, tools, and equipment.
                </p>
              </div>

              <form
                onSubmit={handleSearchSubmit}
                className="rounded-2xl border border-border bg-card p-3 shadow-sm"
                data-testid="form-search-all-categories"
              >
                <div className="flex flex-col lg:flex-row gap-3">
                  <div className="relative flex-1">
                    <Input
                      value={draftKeywords}
                      onChange={e => setDraftKeywords(e.target.value)}
                      placeholder="Search lumber, pipe, windows, tools, vehicles..."
                      className="h-11 bg-background pr-11"
                      data-testid="input-search-all-categories"
                    />
                    {draftKeywords.trim() && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={handleClearSearch}
                        data-testid="button-clear-search-all-categories"
                        aria-label="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <QuickFiltersSheet
                      activeFilterCount={activeFilterCount}
                      totalItems={totalItems}
                      categories={categories}
                      selectedCategoryId={params.pub_categoryLevel1}
                      onSelectCategory={setCategory}
                      onClearCategory={clearCategory}
                      conditionGroupLabel={activeConditionGroupLabel}
                      onSelectConditionGroup={setConditionGroup}
                      selectedPrice={params.price}
                      onSelectPrice={setPrice}
                      pendingMiles={pendingMiles}
                      onSelectDistance={setDistance}
                      nearMeStatus={nearMe.status}
                      isPersistentlyDeclined={nearMe.isPersistentlyDeclined}
                      onAllowLocation={nearMe.request}
                      onDismissLocation={dismissNearMePrompt}
                      isLocationActive={isLocationActive}
                      activeLocationLabel={DEVICE_LOCATION_LABEL}
                      onClearLocation={clearLocation}
                      onReset={clearAllFilters}
                    />
                    <Button
                      type="submit"
                      className="h-11 px-5 font-heading uppercase tracking-wide"
                      data-testid="button-submit-all-categories-search"
                    >
                      Search
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </section>

          <section className="container mx-auto px-4 py-5">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-sm font-heading font-bold uppercase tracking-[0.18em] text-muted-foreground">
                Popular filters
              </span>
              {QUICK_CATEGORIES.map(id => {
                const cat = categories.find(c => c.id === id);
                const label = cat?.name ?? id;
                const isActive = params.pub_categoryLevel1 === id;
                return (
                  <Button
                    key={id}
                    type="button"
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full font-heading uppercase tracking-wide"
                    onClick={() => (isActive ? clearCategory() : setCategory(id))}
                    data-testid={`button-quick-category-${id}`}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>

            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm md:p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="text-sm uppercase tracking-[0.18em] text-muted-foreground font-heading font-bold">
                    {params.keywords ? `Results for "${params.keywords}"` : 'All listings'}
                  </p>
                  <h2 className="mt-2 text-2xl md:text-3xl font-heading font-bold uppercase text-foreground">
                    {searchInProgress
                      ? 'Loading…'
                      : totalItems != null
                      ? `${totalItems} Results`
                      : `${listings.length} Results`}
                  </h2>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div>
                    <label
                      htmlFor="search-sort-results"
                      className="mb-1 block text-xs font-heading font-bold uppercase tracking-[0.18em] text-muted-foreground"
                    >
                      Sort by
                    </label>
                    <Select
                      value={params.sort || '__relevance__'}
                      onValueChange={v => setParams({ sort: v === '__relevance__' ? '' : v, page: 1 })}
                    >
                      <SelectTrigger
                        id="search-sort-results"
                        className="h-11 min-w-[200px]"
                        data-testid="select-sort-results-toolbar"
                      >
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        {SORT_OPTIONS.map(opt => (
                          <SelectItem
                            key={opt.value || '__relevance__'}
                            value={opt.value || '__relevance__'}
                          >
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {activeFilterCount > 0 && (
                    <Button
                      type="button"
                      variant="default"
                      className="h-11 px-4 font-heading uppercase tracking-wide"
                      onClick={clearAllFilters}
                      data-testid="button-reset-active-filters-search"
                    >
                      <X className="mr-2 h-4 w-4" /> Reset Filters
                    </Button>
                  )}
                  {isAuthenticated && (
                    <Button
                      type="button"
                      variant={alreadySaved || justSaved ? 'secondary' : 'outline'}
                      className="h-11 px-4 font-heading uppercase tracking-wide"
                      onClick={handleSaveSearch}
                      disabled={createSavedSearchState.isLoading || justSaved || alreadySaved}
                      data-testid="button-save-search"
                    >
                      {justSaved || alreadySaved ? (
                        <>
                          <Check className="mr-2 h-4 w-4" /> Saved
                        </>
                      ) : (
                        <>
                          <BellPlus className="mr-2 h-4 w-4" />
                          {createSavedSearchState.isLoading ? 'Saving…' : 'Save Search'}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
                {activeCategory && (
                  <Badge variant="secondary" className="gap-2 rounded-full px-3 py-1">
                    {activeCategory.name}
                    <button type="button" onClick={clearCategory} aria-label="Remove category filter">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </Badge>
                )}
                {activeConditionGroupLabel && (
                  <Badge variant="secondary" className="rounded-full px-3 py-1">
                    {activeConditionGroupLabel}
                  </Badge>
                )}
                {activePriceLabel && (
                  <Badge variant="secondary" className="rounded-full px-3 py-1">
                    {activePriceLabel}
                  </Badge>
                )}
                {params.origin && (
                  <Badge variant="secondary" className="rounded-full px-3 py-1">
                    Near {params.address || 'location'}
                  </Badge>
                )}
                {activeFilterCount === 0 && (
                  <span
                    className="text-sm text-muted-foreground"
                    data-testid="text-no-active-filters-search"
                  >
                    No filters applied.
                  </span>
                )}
              </div>
            </div>

            <div className="mt-6">
              {listings.length === 0 && searchInProgress ? (
                <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-10 text-center">
                  <p className="text-muted-foreground">Loading listings…</p>
                </div>
              ) : listings.length === 0 ? (
                <div
                  className="rounded-2xl border border-dashed border-border bg-muted/20 p-10 text-center"
                  data-testid="empty-search-results"
                >
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold uppercase">No exact matches yet</h3>
                  <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                    Try broadening your search term or clearing a few filters to see more listings across all categories.
                  </p>
                  <Button
                    type="button"
                    className="mt-6 font-heading uppercase tracking-wide"
                    onClick={clearAllFilters}
                    data-testid="button-clear-filters-empty-state"
                  >
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className={`relative transition-opacity duration-200${searchInProgress ? ' opacity-50 pointer-events-none' : ''}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {listings.map((listing: any) => {
                    const card = mapListingToCardProps(listing, { categories });
                    return (
                      <MarketplaceListingCard
                        key={card.id}
                        listingId={card.id}
                        listingSlug={card.slug}
                        title={card.title}
                        image={card.imgUrl}
                        preloadSrc={card.preloadSrc}
                        priceLabel={card.priceLabel}
                        condition={card.condition}
                        location={card.location}
                        category={card.category}
                        posted={card.posted}
                        sellerRating={card.reviewAvg}
                        sellerReviews={card.reviewCount}
                        membershipTier={card.membershipTier}
                        isVerified={card.isVerified}
                        dataTestId={`card-search-listing-${card.id}`}
                        titleTestId={`text-listing-title-${card.id}`}
                        priceTestId={`text-listing-price-${card.id}`}
                      />
                    );
                  })}
                </div>
                </div>
              )}

              {pagination && pagination.totalPages > 1 && (
                <div className="mt-8 flex justify-center gap-2">
                  {params.page > 1 && (
                    <Button
                      variant="outline"
                      className="font-heading uppercase tracking-wide"
                      onClick={() => setParams({ page: params.page - 1 })}
                    >
                      Previous
                    </Button>
                  )}
                  <span className="flex items-center px-4 text-sm text-muted-foreground">
                    Page {params.page} of {pagination.totalPages}
                  </span>
                  {params.page < pagination.totalPages && (
                    <Button
                      variant="outline"
                      className="font-heading uppercase tracking-wide"
                      onClick={() => setParams({ page: params.page + 1 })}
                    >
                      Next
                    </Button>
                  )}
                </div>
              )}
            </div>
          </section>
        </main>
      </div>

      <FooterContainer />
    </Page>
  );
}

export default function CustomSearchPage(props: any) {
  return (
    <NuqsAdapter>
      <CustomSearchPageInner {...props} />
    </NuqsAdapter>
  );
}
